<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { UISwitch } from "@gohighlevel/ghl-ui";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useAppStore();

const autoBgFromImage = computed({
  get: () => Boolean((store.submitMessageStyle as any).autoBgFromImage),
  set: (value: boolean) => {
    (store.submitMessageStyle as any).autoBgFromImage = value;
  },
});

const getCurrentBgUrl = (): string => {
  const mode = String((store as any).deviceMode || "desktop");
  const url =
    mode === "mobile"
      ? store.formStyle?.mobileBgImage || store.formStyle?.bgImage || ""
      : store.formStyle?.bgImage || "";
  return (url || "").trim();
};

const hasBgImage = computed(() => getCurrentBgUrl().length > 0);
const hasUserToggledAutoBg = ref(false);
const isUserAutoBgDisabled = computed(() =>
  Boolean((store.submitMessageStyle as any).autoBgFromImageExplicitlyDisabled)
);

const onToggleAutoBg = (value: boolean) => {
  autoBgFromImage.value = value;
  if (value) {
    delete (store.submitMessageStyle as any).autoBgFromImageColorLocked;
    (store.submitMessageStyle as any).autoBgFromImageExplicitlyDisabled = false;
  } else {
    (store.submitMessageStyle as any).autoBgFromImageExplicitlyDisabled = true;
  }
  hasUserToggledAutoBg.value = true;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

// Automatically enable background blur when a background image is available
watch(
  hasBgImage,
  (hasImage) => {
    if (
      hasImage &&
      !hasUserToggledAutoBg.value &&
      !autoBgFromImage.value &&
      !isUserAutoBgDisabled.value
    ) {
      autoBgFromImage.value = true;
    }
  },
  { immediate: true }
);
</script>

<template>
  <section v-if="hasBgImage" class="flex flex-col items-start justify-center gap-2 self-stretch">
    <div class="flex items-center gap-2">
      <UISwitch
        id="form_builder-switch-auto_bg_from_image"
        :value="autoBgFromImage"
        @update:value="onToggleAutoBg"
      />
      <span class="text-sm font-medium">{{ t("common.messageBackgroundBlur") }}</span>
    </div>
    <p class="mb-0 mt-1 text-sm text-gray-500">
      {{ t("common.pickBgFromImage") }}
    </p>
  </section>
</template>

<style scoped></style>
